import { h } from 'preact';
import { MOVIE_PLATFORM_TYPE, LOCAL_MOVIE_TYPE, LINK_OUT_TYPE } from 'utils/constants';
import { createMovie } from 'utils/data';
import { alert, Confirm } from 'utils/modal';
import slugify from 'utils/slugify';
import { getCurrentMovieUrl } from 'utils/studioConfig';

import Button from 'components/button/button';
import CardSelectField from 'components/editor/settings/cardSelectField';
import DateField from 'components/editor/settings/dateField';
import SimpleField from 'components/editor/settings/simpleField';
import { MoviePlatformHeader, LocalMovieHeader, LinkOutHeader } from 'components/movies/addMovieModal/customSvgs';

import s from 'components/movies/addMovieModal/addMovieModal.sss';

export default class AddMovieModal extends Confirm {
	s = s;

	get title() {
		return 'New movie';
	}

	get fields() {
		return {};
	}

	constructor(props) {
		super(props);
		this.state = {
			isCreating: false,
			showLongWait: false,
			data: {},
			errors: {},
		};

		// Field renderers
		this.renderMovieTypeField = this.renderMovieTypeField.bind(this);
		this.renderTitleField = this.renderTitleField.bind(this);
		this.renderReleaseDateField = this.renderReleaseDateField.bind(this);
		this.renderSlugField = this.renderSlugField.bind(this);
		this.renderExternalUrlField = this.renderExternalUrlField.bind(this);

		// Event handlers
		this.createMovie = this.createMovie.bind(this);
		this.onFieldInput = this.onFieldInput.bind(this);
		this.onTypeInput = this.onTypeInput.bind(this);
		this.onTitleInput = this.onTitleInput.bind(this);
		this.onSlugInput = this.onSlugInput.bind(this);
	}

	componentWillUnmount() {
		super.componentWillUnmount?.();
		clearTimeout(this.$longWaitTimer);
	}

	getValue(field) {
		return this.state.data[field];
	}

	setValue(field, value) {
		this.setState({ data: { ...this.state.data, [field]: value } });
		this.validateField(field);
	}

	getError(field) {
		return this.state.errors[field];
	}

	setError(field, error) {
		this.setState({ errors: { ...this.state.errors, [field]: error } });
	}

	getValidators(field) {
		return this.fields[field]?.validators || [];
	}

	slugMatchesTitle() {
		const { title, slug } = this.state.data;
		const titleAsSlug = slugify(title, { removeDiacritics: true });
		return slug === titleAsSlug;
	}

	onFieldInput(e) {
		this.setValue(e.target.name, e.target.value);
	}

	onTypeInput(e) {
		const { name, value } = e.target;
		// Each type renders a different field set, so errors left on fields that are no longer shown would block submit invisibly
		this.setState({ data: { ...this.state.data, [name]: value }, errors: {} });
	}

	onTitleInput(e) {
		const title = e.target.value;
		const updateSlug = this.slugMatchesTitle();

		this.setValue('title', title);
		if (updateSlug) this.setValue('slug', slugify(title, { removeDiacritics: true }));
	}

	onSlugInput(e) {
		const { selectionStart, selectionEnd, value } = e.target;
	
		const newValue = slugify(value, { removeDiacritics: true, allowEdgeDashes: true });
		const lengthDiff = newValue.length - value.length;
	
		this.setValue('slug', newValue);
		e.target.setSelectionRange(selectionStart + lengthDiff, selectionEnd + lengthDiff); // Move cursor to correct position after transformation
	}

	validateField(field) {
		const value = this.getValue(field);
		const validators = this.getValidators(field);
		for (const validator of validators) {
			const error = validator(value);
			if (error) {
				this.setError(field, error);
				return false;
			}
		}
		this.setError(field, null);
		return true;
	}

	validateFields() {
		const fields = Object.keys(this.fields);
		const validationResults = fields.map(field => this.validateField(field));
		return validationResults.every(result => result);
	}

	getNewMovieOptions() {
		return this.state.data;
	}

	async createMovie() {
		const isValid = this.validateFields();
		if (!isValid) return;

		this.setState({ isCreating: true });
		this.$longWaitTimer = setTimeout(() => this.setState({ showLongWait: true }), 60000);
		try {
			const options = this.getNewMovieOptions();
			const newMovie = await createMovie(options);

			if (!newMovie?.id) {
				throw new Error('Failed to create new movie');
			}

			// Close the modal and return the new movie data to the caller
			this.base.close(JSON.stringify(newMovie));
		} catch (err) {
			console.error('Error creating movie', err);
			alert('Sorry, an error occurred while creating a new movie', { title: 'Error' });
		} finally {
			clearTimeout(this.$longWaitTimer);
			this.setState({ isCreating: false, showLongWait: false });
		}
	}

	getCommonFieldProps(field) {
		return {
			key: field,
			name: field,
			value: this.getValue(field),
			onInput: this.onFieldInput,
			error: this.getError(field),
		};
	}

	renderMovieTypeField(opts = {}) {
		return (
			<CardSelectField
				{...this.getCommonFieldProps('type')}
				label="Movie type"
				onInput={this.onTypeInput}
				{...opts}
			>
				<option class={this.s.movieTypeOption} value={MOVIE_PLATFORM_TYPE} label="Full movie" image={<MoviePlatformHeader />} description="Global, full-feature site. Custom styling and pages available." />
				<option class={this.s.movieTypeOption} value={LOCAL_MOVIE_TYPE} label="Local movie" image={<LocalMovieHeader />} subtext="(Locally funded)" description="Streamlined for local titles only." />
				{/* <option class={this.s.movieTypeOption} value={LINK_OUT_TYPE} label="Link out" image={<LinkOutHeader />} description="A quick redirect that sends visitors to an external link." /> */}
			</CardSelectField>
		);
	}

	renderTitleField(opts = {}) {
		return (
			<SimpleField
				{...this.getCommonFieldProps('title')}
				label="Movie title"
				onInput={this.onTitleInput}
				{...opts}
			/>
		);
	}

	renderReleaseDateField(opts = {}) {
		return (
			<DateField
				{...this.getCommonFieldProps('release')}
				label="Release date"
				help="The date the movie releases"
				allowCustomPrecision
				{...opts}
			/>
		);
	}

	renderSlugField(opts = {}) {
		return (
			<SimpleField
				{...this.getCommonFieldProps('slug')}
				label="Movie URL"
				preText={getCurrentMovieUrl()}
				onInput={this.onSlugInput}
				{...opts}
			/>
		);
	}

	renderExternalUrlField(opts = {}) {
		return (
			<SimpleField
				{...this.getCommonFieldProps('url')}
				type="url"
				label="Use external URL"
				help="Sets a redirect to the specified URL"
				placeholder="https://www.example.com/"
				toggleable
				{...opts}
			/>
		);
	}

	renderContent() {
		return (
			<div class={this.s.content}>
				{Object.values(this.fields).map(field => field.render())}
			</div>
		);
	}

	renderActionButton() {
		const { errors, isCreating } = this.state;
		const hasErrors = Object.values(errors).some(error => !!error);
		const disabled = isCreating || hasErrors;

		return (
			<Button
				type="button"
				primary
				iconAlt="chevron-right"
				disabled={disabled}
				loading={isCreating}
				onClick={this.createMovie}
			>
				{isCreating ? 'Creating movie' : 'Create movie'}
			</Button>
		);
	}

	renderActions() {
		const base = super.renderActions();
		if (base && this.state.showLongWait) {
			base.children.unshift(<span class={this.s.loadingMessage}>We're still working on it...</span>);
		}
		return base;
	}
}
